import { Component, OnInit } from "@angular/core";
import { UtilsService } from "../../../../auth/service/utils.service";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthenticationService } from "../../../../auth/service";
import { locale as italian } from "../../../../i18n/it";
import { IContentHeader } from "../../../../layout/components/content-header/content-header.component";
import { IBank, IBankForm } from "../../../../auth/models/bank";
import { IDatatableConfig } from "../../../../auth/models/datatable";
import { ListService } from "../../../../auth/service/list.service";

@Component({
  selector: "app-banks",
  templateUrl: "./banks-list.component.html",
  styleUrls: ["./banks-list.component.scss"],
})
export class BanksListComponent implements OnInit {
  public config: IDatatableConfig;
  public contentHeader: IContentHeader;
  public banksList: IBank[] = [];
  public tableLimit: number = 10;
  public searchKeyword: string = "";
  public searchStatus: number = 2;
  public bankForm: IBankForm = {
    id: null,
    name: null,
    other: {
      iban: null,
      bic: null,
      note: null,
    },
  };
  public showModalError: boolean = false;
  public filteredBanksList: IBank[] = [];
  public selectedNote: string = null;
  private list_type: string = "bank";

  constructor(
    public _listService: ListService,
    public _utilService: UtilsService,
    private _routerService: Router,
    public _modalService: NgbModal,
    public _authenticationService: AuthenticationService,
  ) {
    this.contentHeader = {
      headerTitle: italian.data.MENU.BANKS,
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: italian.data.MENU.MANAGE,
            isLink: true,
            link: "/dashboard/platform-management",
          },
          {
            name: italian.data.MENU.BANKS,
            isLink: false,
          },
        ],
      },
    };
  }

  async ngOnInit() {
    if (!this._authenticationService.hasCapability("show_menu_banks")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }

    await this.loadBanks();
  }

  updateTableLimit(value) {
    this.tableLimit = Number(value);
  }

  async loadBanks() {
    this.banksList = await this._listService.getList(this.list_type);
    this.filterBanks();
  }

  filterBanks() {
    const key: string = this.searchKeyword.trim().toLowerCase();
    let filterItem: IBank[] = this.banksList;

    if (key.length > 0) {
      filterItem = filterItem.filter((item: IBank) => item.name.toLowerCase().trim().includes(key));
    }
    this.filteredBanksList = filterItem;
  }

  isError(): boolean {
    if (
      !this.bankForm.name ||
      this.bankForm.name.length < 1 ||
      !this.bankForm.other.iban ||
      this.bankForm.other.iban.length < 1 ||
      this.bankForm.other.iban.length < 15 ||
      this.bankForm.other.iban.length > 36 ||
      !this.bankForm.other.bic ||
      this.bankForm.other.bic.length < 1 ||
      this.bankForm.other.bic.length < 8 ||
      this.bankForm.other.bic.length > 11
    ) {
      this.showModalError = true;
      return true;
    }
  }

  async addBank() {
    if (!this._authenticationService.hasCapability("create_bank")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }

    if (this.isError()) {
      return false;
    }

    const data: IBankForm = {
      name: this.bankForm.name.trim(),
      other: {
        iban: this.bankForm.other.iban.trim(),
        bic: this.bankForm.other.bic.trim(),
        note: this.bankForm.other.note ? this.bankForm.other.note.trim() : null,
      },
    };
    this._utilService.showLoading();
    await this._listService
      .create(data, this.list_type)
      .then(() => {
        this._modalService.dismissAll();
        this.loadBanks();
        this._utilService.hideLoading();
        this._utilService.showSWALSuccess({ text: "SUCCESS_CREATE_BANK" });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_CREATE_BANK" });
      });
  }

  async editBank() {
    if (!this._authenticationService.hasCapability("edit_bank")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }

    if (this.isError()) {
      return false;
    }

    const data: IBankForm = {
      name: this.bankForm.name.trim(),
      other: {
        iban: this.bankForm.other.iban.trim(),
        bic: this.bankForm.other.bic.trim(),
        note: this.bankForm.other.note ? this.bankForm.other.note.trim() : null,
      },
    };
    this._utilService.showLoading();
    await this._listService
      .update(this.bankForm.id, data, this.list_type)
      .then(() => {
        this.loadBanks();
        this._modalService.dismissAll();
        this._utilService.hideLoading();
        this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_BANK" });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_EDIT_BANK" });
      });
  }

  async deleteBank(currentBank) {
    if (!this._authenticationService.hasCapability("delete_bank")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }

    let deleteText: string = await this._utilService.translate("ALERT.CONFIRM_DELETE_TEXT");
    deleteText += await this._utilService.translate("ALERT.CONFIRM_DELETE_THIS_OBJECT");
    deleteText += currentBank.name;
    await this._utilService
      .showSWALDeleteConfirmation({ text: deleteText })
      .then(async (result) => {
        if (result.value) {
          await this._listService.delete(currentBank.id, this.list_type);
          await this._utilService.showPopupItemDeleted().then(async () => {
            await this.loadBanks();
          });
        } else if (result.isDismissed) {
          await this._utilService.showSWALError({
            title: "ABORTED_TITLE",
            text: "ABORTED_TEXT",
          });
        }
      })
      .catch(() => {
        this._utilService.showSWALError({ text: "ERROR_DELETE_BANK" });
      });
  }

  public modalOpen(modalBasic, currentBank: IBank, selectedNote: string = null) {
    this._modalService.open(modalBasic, {
      size: "md",
      centered: true,
    });
    this.bankForm = {
      name: null,
      other: {
        iban: null,
        bic: null,
        note: null,
      },
    };
    this.selectedNote = selectedNote;
    if (!!currentBank) {
      this.bankForm = {
        id: currentBank.id,
        name: currentBank.name,
        other: {
          iban: currentBank.other.iban,
          bic: currentBank.other.bic,
          note: currentBank.other.note,
        },
      };
    }
    this.showModalError = false;
  }
}
